import { useEffect, useRef } from 'react';
import { POST_MEASURE_ELK_DELAY_MS } from './constants';
import { useFontsReady } from './useFontsReady';
import { getIsolatedMissionIds } from './isolatedSpawnPositions';

/**
 * After all node dimensions are measured AND webfonts have loaded, bump layoutVersion once so
 * ELK re-runs with real sizes. The fonts gate keeps the settled layout deterministic across
 * sessions — pre-swap measurements produce slightly different ELK coordinates every open.
 * @param {import('react').MutableRefObject<boolean>} postMeasureElkRelayoutDoneRef — parent resets on campaign/task set changes.
 * @param {number} layoutVersion — included so a bump from `triggerLayout` clears the pending timer (avoids racing Tidy).
 */
export function usePostMeasureElkRelayout({
  postMeasureElkRelayoutDoneRef,
  taskIdSignature,
  nodeDimensions,
  isMultiCampaignMode,
  tasksForLayoutRef,
  nodeDimensionsRef,
  setLayoutVersion,
  layoutVersion,
  layoutHoldRef = null,
  hasActiveFilters = false,
}) {
  const fontsReady = useFontsReady();
  const prevTaskIdSignatureRef = useRef(taskIdSignature);

  useEffect(() => {
    const prev = prevTaskIdSignatureRef.current;
    prevTaskIdSignatureRef.current = taskIdSignature;
    if (prev === taskIdSignature) return;

    const prevIds = new Set(prev.split('|').filter(Boolean));
    const currentIds = new Set(taskIdSignature.split('|').filter(Boolean));
    const added = [...currentIds].filter((id) => !prevIds.has(id));
    const removed = [...prevIds].filter((id) => !currentIds.has(id));
    const isolated = getIsolatedMissionIds(tasksForLayoutRef.current);
    // A right-click "drop here" only adds a loose card. Re-running the
    // open-campaign measure pass would nudge every other node.
    if (removed.length === 0 && added.length > 0 && added.every((id) => isolated.has(id))) {
      return;
    }
    // Delete already queues an animated Tidy. Resetting this flag fires an
    // implicit remasure ~72ms later that snaps remaining missions.
    if (removed.length > 0 && !hasActiveFilters) {
      return;
    }
    if (layoutHoldRef?.current) {
      return;
    }

    postMeasureElkRelayoutDoneRef.current = false;
  }, [taskIdSignature, postMeasureElkRelayoutDoneRef, tasksForLayoutRef, layoutHoldRef, hasActiveFilters]);

  useEffect(() => {
    if (isMultiCampaignMode) return undefined;
    if (!fontsReady) return undefined;
    const layoutTasks = tasksForLayoutRef.current;
    if (!layoutTasks?.length) return undefined;
    if (layoutHoldRef?.current) return undefined;
    if (postMeasureElkRelayoutDoneRef.current) return undefined;

    const allMeasured = layoutTasks.every((t) => {
      const d = nodeDimensions[t.id];
      return (
        d &&
        Number.isFinite(d.width) &&
        d.width > 0 &&
        Number.isFinite(d.height) &&
        d.height > 0
      );
    });
    if (!allMeasured) return undefined;

    const id = window.setTimeout(() => {
      if (layoutHoldRef?.current) return;
      if (postMeasureElkRelayoutDoneRef.current) return;
      if (isMultiCampaignMode) return;
      const lt = tasksForLayoutRef.current;
      if (!lt?.length) return;
      const nd = nodeDimensionsRef.current;
      const stillAll = lt.every((t) => {
        const d = nd[t.id];
        return (
          d &&
          Number.isFinite(d.width) &&
          d.width > 0 &&
          Number.isFinite(d.height) &&
          d.height > 0
        );
      });
      if (!stillAll) return;

      postMeasureElkRelayoutDoneRef.current = true;
      setLayoutVersion((v) => v + 1);
    }, POST_MEASURE_ELK_DELAY_MS);

    return () => window.clearTimeout(id);
  }, [
    taskIdSignature,
    nodeDimensions,
    isMultiCampaignMode,
    fontsReady,
    tasksForLayoutRef,
    nodeDimensionsRef,
    setLayoutVersion,
    postMeasureElkRelayoutDoneRef,
    layoutVersion,
    layoutHoldRef,
    hasActiveFilters,
  ]);
}
